/**
 * Hands for modular figures, drawn at the figure's full resolution (`RIG_RES` canvas pixels per art
 * pixel), so a hand has room for a thumb, knuckles and fingers.
 *
 * A hand is built in its own frame from tapering capsules — the palm, the fingers, the thumb —
 * with `u` across the hand toward the thumb and `v` down the forearm toward the fingertips, in
 * canvas pixels at double resolution from the middle of the wrist. It is laid along its forearm at
 * whatever angle that takes, and mirrored so the thumb comes out where the pose and view put it:
 * forward for a hanging or raised hand in the turned views, toward the body head-on, and up for a
 * hand held out level. Every shape is built as a right hand seen from its back (`back`) and as a
 * left hand seen from its palm (`palm`); which one is drawn follows from which hand it is and
 * whether it is mirrored, so a mirrored view never draws a right hand as a left one.
 *
 * Light is laid on once the hand lies on the canvas, so it always comes from the upper right: an
 * outline that is a dark warm brown of the skin on the shadow side and the skin's shade on the lit
 * side, a lit rim toward the light and a shaded one away from it. Where a part lies over another (a
 * thumb across the fingers, a finger beside the next) a line or a crease parts them. The wrist end
 * is left open, so the hand runs on from the forearm without a notch.
 *
 * The pose picks the shape, as it picks the face's mood: a weapon hand grips in a cut, an empty
 * one makes a fist; a palm strike opens the hand; a casting hand points two fingers (剑指).
 */
import { fromHsl, shade, toHsl } from '../palette';
import type { Grid } from './hair';
import { BODY, type Side, type Skeleton } from './skeleton';

export type HandShape = 'relaxed' | 'fist' | 'grip' | 'palm' | 'clasp' | 'fingers';

type P = readonly [number, number];

/** One rounded part of a hand: from `a` to `b` (u, v), `r0` → `r1` across. */
interface Part {
  a: P;
  b: P;
  r0: number;
  r1: number;
  /** Nearer the eye than parts with lower `z`. */
  z: number;
  /** Seen from the palm side (its rosier skin). */
  palm?: boolean;
  /** How its edge shows where it lies over a part behind it, or beside one of its own depth. */
  edge?: 'line' | 'crease';
  /** Parts of one group show as one (no line between them). */
  group?: number;
}

/** A hand shape seen from one side: its parts, knuckle lights and creases. */
interface Build {
  parts: Part[];
  knuckles?: P[];
  creases?: (readonly [P, P])[];
}

export interface HandDesign {
  id: HandShape;
  label: string;
  /** A right hand hanging down seen from its back; a left hand seen from its palm (thumb +u). */
  build: (face: 'back' | 'palm') => Build;
  /**
   * Held out level, when the shape bends at the wrist rather than lying along the forearm (an open
   * palm pushes heel first, the fingers standing up toward the thumb's side).
   */
  push?: (face: 'back' | 'palm') => Build;
}

const cap = (a: P, b: P, r0: number, r1: number, z: number, more: Partial<Part> = {}): Part => ({
  a,
  b,
  r0,
  r1,
  z,
  ...more,
});

/**
 * The back of the hand from the wrist to the knuckles at `v`, flaring from the wrist's width; its
 * rounded end stops just past the knuckles, under the fingers.
 */
const palmBlock = (v: number, z: number, palm?: boolean) =>
  cap([0, -3], [0.3, v - 2.4], 3, 3.6, z, { palm, edge: 'crease' });

/** Four fingers from the knuckles: [u at the knuckle, v at the knuckle, length], index first. */
function fingers(
  spec: readonly (readonly [number, number, number])[],
  z: number,
  r: number,
  groups: readonly number[],
  palm?: boolean,
  lean = 0,
): Part[] {
  // Held together, the fingers lean in a little toward the middle finger, so the hand narrows to its tips.
  return spec.map(([u, v, len], i) =>
    cap([u, v], [u * 0.85 + lean * len, v + len], r, r * 0.8, z, {
      edge: 'crease',
      group: 10 + groups[i]!,
      palm,
    }),
  );
}

/** The four knuckles across the back of a closed hand. */
const KNUCKLES: P[] = [
  [2.3, 3.6],
  [0.8, 3.4],
  [-0.7, 3.4],
  [-2.2, 3.7],
];

/** The folded fingers of a closed hand, as a band across the end of the palm. */
const fold = (v0: number, v1: number, z: number, palm?: boolean, edge?: 'line') =>
  cap([-1.3, (v0 + v1) / 2], [1.4, (v0 + v1) / 2], (v1 - v0) / 2 + 0.2, (v1 - v0) / 2 + 0.2, z, {
    palm,
    edge,
  });

/** Creases between the folded fingers, from `v0` to `v1`. */
const foldCreases = (v0: number, v1: number): (readonly [P, P])[] =>
  [0.8, -0.8].map(
    (u) =>
      [
        [u, v0 + (v1 - v0) * 0.4],
        [u, v1],
      ] as const,
  );

export const HANDS: readonly HandDesign[] = [
  // The fingers hang loose, a little curled toward the palm so they show shorter than they are, the
  // first two and the last two together; the thumb lies along the side, its tip by the first knuckle.
  {
    id: 'relaxed',
    label: 'ปล่อยสบาย',
    build: (face) =>
      face === 'back'
        ? {
            parts: [
              cap([2.4, 0.8], [3.5, 6.4], 1.5, 1.0, 0),
              palmBlock(4.6, 1, false),
              ...fingers(
                [
                  [2.0, 5.2, 5.0],
                  [0.6, 5.5, 5.6],
                  [-0.9, 5.3, 5.2],
                  [-2.2, 4.8, 4.2],
                ],
                1,
                1.05,
                [0, 0, 1, 1],
              ),
            ],
          }
        : {
            parts: [
              palmBlock(4.6, 0, true),
              ...fingers(
                [
                  [2.0, 5.2, 4.4],
                  [0.6, 5.5, 5.0],
                  [-0.9, 5.3, 4.6],
                  [-2.2, 4.8, 3.8],
                ],
                0.5,
                1.05,
                [0, 0, 1, 1],
                true,
              ),
              // The ball of the thumb over the palm's edge.
              cap([2.2, 0.6], [3.3, 6.0], 1.7, 1.0, 1, { edge: 'line' }),
            ],
            creases: [
              [
                [-2.6, 4.5],
                [0.2, 4.2],
              ],
            ],
          },
  },
  // From the back, the four knuckles in a row over the folded fingers; from the front, the folded
  // fingers side by side and the thumb laid across them.
  {
    id: 'fist',
    label: 'กำหมัด',
    build: (face) =>
      face === 'back'
        ? {
            parts: [palmBlock(4.0, 0), fold(3.0, 7.4, 1), cap([2.9, 1.4], [3.6, 5.4], 1.4, 1.1, 0.5)],
            knuckles: KNUCKLES,
            creases: foldCreases(4.6, 7.4),
          }
        : {
            parts: [
              palmBlock(4.0, 0, true),
              fold(3.0, 7.4, 0.5, false, 'line'),
              cap([3.5, 1.8], [-0.2, 4.6], 1.45, 1.1, 1, { edge: 'line' }),
            ],
            creases: foldCreases(4.6, 7.4),
          },
  },
  // Closed round a handle, which runs across the middle of the hand: the knuckles over it from the
  // back; from the front the fingers wrapped round it and the thumb over them.
  {
    id: 'grip',
    label: 'จับด้าม',
    build: (face) =>
      face === 'back'
        ? {
            parts: [palmBlock(4.0, 0), fold(3.0, 8.0, 1), cap([2.9, 1.4], [3.7, 5.0], 1.4, 1.15, 0.5)],
            knuckles: KNUCKLES,
            creases: foldCreases(5.2, 8.0),
          }
        : {
            parts: [
              palmBlock(4.0, 0, true),
              fold(3.0, 8.0, 0.5, false, 'line'),
              cap([3.6, 2.2], [-1.0, 5.4], 1.4, 1.1, 1, { edge: 'line' }),
            ],
            creases: foldCreases(5.4, 8.0),
          },
  },
  // Open, the fingers held straight and together, the thumb against the side.
  {
    id: 'palm',
    label: 'แบฝ่ามือ',
    build: (face) => {
      const straight = (palm: boolean) =>
        fingers(
          [
            [2.0, 5.2, 5.4],
            [0.6, 5.5, 6.0],
            [-0.9, 5.3, 5.6],
            [-2.2, 4.8, 4.4],
          ],
          palm ? 0.5 : 1,
          1.0,
          [0, 1, 2, 3],
          palm,
        );
      return face === 'back'
        ? { parts: [cap([2.6, 0.8], [3.9, 6.0], 1.45, 1.0, 0), palmBlock(4.6, 1), ...straight(false)] }
        : {
            parts: [
              palmBlock(4.6, 0, true),
              ...straight(true),
              cap([2.3, 0.6], [3.6, 5.6], 1.7, 1.0, 1, { edge: 'line' }),
            ],
            creases: [
              [
                [-2.6, 4.3],
                [0.4, 3.9],
              ],
            ],
          };
    },
    // The palm strike: the wrist bent back, the heel of the hand leading, the fingers standing up.
    push: (face) => {
      const up = (palm: boolean): Part[] =>
        [0.5, 1.9, 3.3, 4.6].map((v, i) =>
          cap([4.5, v], [9.6 - (i === 3 ? 1.2 : i === 0 ? 0.4 : 0), v], 0.95, 0.85, palm ? 0.5 : 1, {
            edge: 'crease',
            group: 10 + i,
            palm,
          }),
        );
      return face === 'back'
        ? {
            parts: [
              cap([-0.8, 2.4], [4.2, 2.4], 3.2, 3.0, 1),
              ...up(false),
              cap([1.0, 5.0], [4.6, 6.2], 1.3, 1.0, 0),
            ],
          }
        : {
            parts: [
              cap([-0.8, 2.4], [4.2, 2.4], 3.2, 3.0, 0, { palm: true }),
              ...up(true),
              cap([0.8, 5.0], [4.6, 6.4], 1.5, 1.0, 1, { edge: 'line' }),
            ],
          };
    },
  },
  // The salute (抱拳): the right fist held in the left hand, the left fingers wrapped across its
  // front and the left thumb over the top.
  {
    id: 'clasp',
    label: 'ประสานมือ',
    build: (face) => ({
      parts: [
        palmBlock(4.0, 0, face === 'palm'),
        fold(3.0, 7.6, 0.5),
        cap([-3.4, 6.0], [2.2, 6.0], 1.9, 1.9, 1, { edge: 'line' }),
        cap([-4.0, 2.6], [-1.2, 3.6], 1.2, 1.0, 1.5, { edge: 'line' }),
      ],
      knuckles: [
        [2.0, 3.6],
        [0.6, 3.4],
      ],
      creases: [
        [
          [-4.2, 6.2],
          [2.6, 6.2],
        ],
      ],
    }),
  },
  // Sword fingers (剑指): the first two fingers straight and together, the other two folded under
  // the thumb.
  {
    id: 'fingers',
    label: 'ดาบนิ้ว',
    build: (face) => {
      const two = (palm: boolean) =>
        [
          [1.7, 4.6, 6.4],
          [0.2, 4.9, 6.8],
        ].map(([u, v, len], i) =>
          cap([u!, v!], [u!, v! + len!], 1.0, 0.85, 1, { edge: 'crease', group: 10 + i, palm }),
        );
      return face === 'back'
        ? {
            parts: [
              cap([2.8, 1.2], [3.4, 5.2], 1.35, 1.0, 0),
              palmBlock(3.0, 0.5),
              cap([-2.5, 4.6], [-1.0, 4.9], 1.7, 1.7, 0.8),
              ...two(false),
            ],
            knuckles: [
              [-2.3, 3.3],
              [-0.9, 3.2],
            ],
          }
        : {
            parts: [
              palmBlock(3.0, 0, true),
              cap([-2.5, 4.6], [-1.0, 4.9], 1.7, 1.7, 0.5),
              ...two(true),
              cap([3.0, 2.0], [-1.8, 5.0], 1.35, 1.0, 1.5, { edge: 'line' }),
            ],
          };
    },
  },
];

export const HAND_BY_ID: Readonly<Record<HandShape, HandDesign>> = Object.fromEntries(
  HANDS.map((h) => [h.id, h]),
) as Record<HandShape, HandDesign>;

// ── colour ──

/** The skin tones a hand is drawn in, from the figure's skin and its shade. */
export interface HandTones {
  light: string;
  skin: string;
  palm: string;
  shade: string;
  crease: string;
  line: string;
}

export function handTones(skin: string, skinS: string): HandTones {
  const [h, s, l] = toHsl(skinS);
  return {
    light: shade(skin, 0.05),
    skin,
    palm: mixHex(skin, '#ff9c8c', 0.1),
    shade: skinS,
    crease: fromHsl([h - 6, s * 0.75, l * 0.8]),
    // The outline: a dark, warm brown of the skin, never black.
    line: fromHsl([h - 10, s * 0.55, l * 0.55]),
  };
}

function mixHex(a: string, b: string, k: number): string {
  const ch = (h: string, i: number) => parseInt(h.slice(i, i + 2), 16);
  return `#${[1, 3, 5]
    .map((i) =>
      Math.round(ch(a, i) * (1 - k) + ch(b, i) * k)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

// ── laying a hand on the canvas ──

/** A hand laid out in canvas pixels: colours by row, from (`x`, `y`) at the frame's resolution. */
export interface HandSprite {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Row-major colours, `undefined` where the hand leaves the canvas bare. */
  px: (string | undefined)[];
}

/** How far a point lies inside a part (> 0 inside). */
function inside(p: Part, u: number, v: number): number {
  const dx = p.b[0] - p.a[0];
  const dy = p.b[1] - p.a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((u - p.a[0]) * dx + (v - p.a[1]) * dy) / l2)) : 0;
  const d = Math.hypot(u - (p.a[0] + dx * t), v - (p.a[1] + dy * t));
  return p.r0 + (p.r1 - p.r0) * t - d;
}

/** How far along a part a point lies, 0 at `a` to 1 at `b`. */
function along(p: Part, u: number, v: number): number {
  const dx = p.b[0] - p.a[0];
  const dy = p.b[1] - p.a[1];
  const l2 = dx * dx + dy * dy;
  return l2 ? Math.max(0, Math.min(1, ((u - p.a[0]) * dx + (v - p.a[1]) * dy) / l2)) : 0;
}

/** Where a hand lies: its wrist on the canvas, its axes there and its size per local unit. */
interface Lay {
  wx: number;
  wy: number;
  /** Down the hand (`v`) and across it toward the thumb (`u`), on screen. */
  ux: number;
  uy: number;
  ex: number;
  ey: number;
  /** Canvas pixels per local unit across, and along (less when the hand is foreshortened). */
  s: number;
  sv: number;
}

/**
 * Rasterizes a build: every pixel's nearest part, then lines between parts, the outline, light and
 * the knuckles and creases.
 */
function raster(b: Build, lay: Lay, tones: HandTones): HandSprite {
  const { wx, wy, ux, uy, ex, ey, s, sv } = lay;
  const R = Math.ceil(13 * Math.max(s, sv)) + 2;
  const x0 = Math.floor(wx - R);
  const y0 = Math.floor(wy - R);
  const W = R * 2 + 1;
  const H = W;
  /** A canvas pixel's centre in the hand's frame. */
  const local = (X: number, Y: number): [number, number] => {
    const dx = X + 0.5 - wx;
    const dy = Y + 0.5 - wy;
    return [(dx * ex + dy * ey) / s, (dx * ux + dy * uy) / sv];
  };
  const top = new Int16Array(W * H).fill(-1);
  const vOf = new Float32Array(W * H);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const [u, v] = local(x0 + x, y0 + y);
      const i = y * W + x;
      vOf[i] = v;
      // Nothing behind the wrist: the forearm is there.
      if (v < -0.6) continue;
      let best = -1;
      for (let k = 0; k < b.parts.length; k++)
        if (inside(b.parts[k]!, u, v) > 0 && (best < 0 || b.parts[k]!.z >= b.parts[best]!.z)) best = k;
      top[i] = best;
    }
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && top[y * W + x]! >= 0;
  // Up the forearm the hand runs on into it: nothing there is open.
  const open = (x: number, y: number) => !solid(x, y) && local(x0 + x, y0 + y)[1] >= -0.6;
  const groupOf = (k: number) => b.parts[k]!.group ?? k;
  const px: (string | undefined)[] = Array(W * H).fill(undefined);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      const k = top[i]!;
      if (k < 0) continue;
      const part = b.parts[k]!;
      const lit = open(x + 1, y) || open(x, y - 1);
      const dark = open(x - 1, y) || open(x, y + 1);
      // Close to the wrist, the outline softens into the forearm's own edge.
      const nearWrist = vOf[i]! < 1.6;
      if (lit || dark) {
        px[i] = nearWrist ? (dark ? tones.shade : tones.skin) : dark ? tones.line : tones.shade;
        continue;
      }
      // Where this part lies over another, or beside another of its depth, its edge shows.
      // Fingers side by side part only toward their tips, where they come apart.
      let edge: 'line' | 'crease' | undefined;
      if (part.edge) {
        const tip = along(part, ...local(x0 + x, y0 + y)) > 0.45;
        for (const [dx, dy] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ] as const) {
          const n = top[(y + dy) * W + x + dx];
          if (n === undefined || n < 0 || groupOf(n) === groupOf(k)) continue;
          const other = b.parts[n]!;
          const fingersApart = tip && groupOf(k) >= 10 && groupOf(n) >= 10 && groupOf(k) > groupOf(n);
          if (part.z > other.z || (part.z === other.z && fingersApart)) edge = part.edge;
        }
      }
      if (edge) {
        px[i] = edge === 'line' ? tones.shade : tones.crease;
        continue;
      }
      const base = part.palm ? tones.palm : tones.skin;
      // A lit rim toward the light; the outline is the shadow side's dark.
      const rimLit = open(x + 2, y) || open(x, y - 2);
      const rimDark = open(x - 2, y) || open(x, y + 2);
      px[i] = rimLit && !rimDark && !nearWrist ? tones.light : base;
    }
  /** The pixel a point of the hand's frame lands on, if it is the hand's and not its outline. */
  const at = (u: number, v: number): number | undefined => {
    const X = Math.floor(wx + (u * s * ex + v * sv * ux)) - x0;
    const Y = Math.floor(wy + (u * s * ey + v * sv * uy)) - y0;
    if (!solid(X, Y) || open(X + 1, Y) || open(X - 1, Y) || open(X, Y + 1) || open(X, Y - 1))
      return undefined;
    return Y * W + X;
  };
  for (const [a, c] of b.creases ?? []) {
    const n = Math.ceil(Math.hypot(c[0] - a[0], c[1] - a[1]) * Math.max(s, sv) * 2);
    for (let t = 0; t <= n; t++) {
      const i = at(a[0] + ((c[0] - a[0]) * t) / n, a[1] + ((c[1] - a[1]) * t) / n);
      if (i !== undefined && px[i] !== tones.line) px[i] = tones.crease;
    }
  }
  for (const [u, v] of b.knuckles ?? []) {
    const i = at(u, v);
    if (i !== undefined) px[i] = tones.light;
  }
  return { x: x0, y: y0, w: W, h: H, px };
}

/**
 * Which way the thumb should point on screen for a hand of `side` in this view: forward (+x) and
 * up in the turned views; head-on, toward the middle of the body and up.
 */
function thumbWay(sk: Skeleton, side: Side): [number, number] {
  if (!sk.square) return [1, -1];
  // Head-on the right side (A) is on the screen's left facing the camera, on its right from behind.
  const inward = (side === 'A') === !sk.back ? 1 : -1;
  return [inward, -1];
}

/** A hand of `side` in `shape` pointing along (`ux`, `uy`) from the wrist at (`wx`, `wy`) canvas pixels. */
function layHand(
  shape: HandShape,
  side: Side,
  thumb: readonly [number, number],
  dx: number,
  dy: number,
  wx: number,
  wy: number,
  res: number,
  tones: HandTones,
): HandSprite {
  const len = Math.hypot(dx, dy);
  // Seen end on (a hand reaching at the camera), the hand hangs as drawn.
  let [ux, uy] = len < 0.5 ? [0, 1] : [dx / len, dy / len];
  // Foreshortened along its length as the forearm is.
  const fore = len < 0.5 ? 1 : Math.max(0.6, Math.min(1, len / BODY.hand));
  // Laid at the nearest eighth of a turn, where its pixels step cleanly (straight, or diagonal one
  // for one); the wrist takes up the rest, as a real wrist bends a little off the forearm.
  const eighth = Math.round(Math.atan2(uy, ux) / (Math.PI / 4)) * (Math.PI / 4);
  [ux, uy] = [Math.cos(eighth), Math.sin(eighth)];
  if (Math.abs(ux) < 1e-9) ux = 0;
  if (Math.abs(uy) < 1e-9) uy = 0;
  // The thumb (the hand's +u) goes to whichever side across the forearm the pose wants it.
  const flip = uy * thumb[0] - ux * thumb[1] < 0;
  const ex = flip ? -uy : uy;
  const ey = flip ? ux : -ux;
  // A right hand unmirrored shows its back; mirrored, its palm. A left hand the other way round.
  const face = (side === 'A') !== flip ? 'back' : 'palm';
  const d = HAND_BY_ID[shape];
  const level = Math.abs(ux) > Math.abs(uy) * 1.2;
  const b = (level && d.push ? d.push : d.build)(face);
  const s = res / 2;
  return raster(b, { wx, wy, ux, uy, ex, ey, s, sv: s * fore }, tones);
}

/**
 * The hand of `side` in `shape`, laid along its forearm and lit, at `res` canvas pixels per art
 * pixel (the frame's top padding is the caller's).
 */
export function handSprite(
  sk: Skeleton,
  side: Side,
  shape: HandShape,
  tones: HandTones,
  res: number,
): HandSprite {
  const wrist = sk.joints[`wrist${side}`];
  const hand = sk.joints[`hand${side}`];
  // The wrist at the middle of its art pixel, where the forearm drawn at art resolution ends.
  const wx = (Math.round(wrist.x) + 0.5) * res;
  const wy = (Math.round(wrist.y) + 0.5) * res;
  // A left-facing view is drawn mirrored (rig/dir8.ts): there the right hand is drawn as a left hand, and so on.
  const drawnAs: Side = sk.left ? (side === 'A' ? 'B' : 'A') : side;
  return layHand(shape, drawnAs, thumbWay(sk, side), hand.x - wrist.x, hand.y - wrist.y, wx, wy, res, tones);
}

/** Draws a hand sprite, moved by (`dx`, `dy`) canvas pixels. */
export function drawHand(g: CanvasRenderingContext2D, sp: HandSprite, dx = 0, dy = 0): void {
  for (let y = 0; y < sp.h; y++)
    for (let x = 0; x < sp.w; x++) {
      const c = sp.px[y * sp.w + x];
      if (!c) continue;
      g.fillStyle = c;
      g.fillRect(sp.x + x + dx, sp.y + y + dy, 1, 1);
    }
}

/**
 * The hand at art resolution (one pixel per art pixel) from its full-resolution sprite: an art
 * pixel where all of its block is hand, in the block's commonest colour. Kept inside the fine hand,
 * so where the fine hand is laid over it nothing of it shows past the fine hand's edge.
 */
export function drawHandArt(g: CanvasRenderingContext2D, sp: HandSprite, res: number): void {
  const X0 = Math.floor(sp.x / res);
  const Y0 = Math.floor(sp.y / res);
  const X1 = Math.ceil((sp.x + sp.w) / res);
  const Y1 = Math.ceil((sp.y + sp.h) / res);
  for (let Y = Y0; Y < Y1; Y++)
    for (let X = X0; X < X1; X++) {
      const count = new Map<string, number>();
      let n = 0;
      for (let v = 0; v < res; v++)
        for (let u = 0; u < res; u++) {
          const x = X * res + u - sp.x;
          const y = Y * res + v - sp.y;
          const c = x >= 0 && y >= 0 && x < sp.w && y < sp.h ? sp.px[y * sp.w + x] : undefined;
          if (!c) continue;
          n++;
          count.set(c, (count.get(c) ?? 0) + 1);
        }
      if (n < res * res) continue;
      g.fillStyle = [...count].sort((a, b) => b[1] - a[1])[0]![0];
      g.fillRect(X, Y, 1, 1);
    }
}

const PREVIEW_TONES = handTones('#eec7a2', '#c9a084');

/**
 * The shape's art-resolution grid for a right hand pointing along (`dx`, `dy`), its thumb forward
 * (`s` skin, `S` outline and shade), for previews such as the studio's hand picker.
 */
export function handGrid(shape: HandShape, dx: number, dy: number): Grid {
  const sp = layHand(shape, 'A', [1, -1], dx, dy, 1, 1, 2, PREVIEW_TONES);
  const dark = new Set([PREVIEW_TONES.line, PREVIEW_TONES.crease, PREVIEW_TONES.shade]);
  const X0 = Math.floor(sp.x / 2);
  const Y0 = Math.floor(sp.y / 2);
  const rows: string[] = [];
  let [c0, c1, r0, r1] = [Infinity, -1, Infinity, -1];
  const cells: string[][] = [];
  for (let Y = Y0; Y < Math.ceil((sp.y + sp.h) / 2); Y++) {
    const row: string[] = [];
    for (let X = X0; X < Math.ceil((sp.x + sp.w) / 2); X++) {
      const block = [0, 1, 2, 3]
        .map((q) => {
          const x = X * 2 + (q & 1) - sp.x;
          const y = Y * 2 + (q >> 1) - sp.y;
          return x >= 0 && y >= 0 && x < sp.w && y < sp.h ? sp.px[y * sp.w + x] : undefined;
        })
        .filter((c): c is string => !!c);
      const ch =
        block.length < 2 ? '.' : block.filter((c) => dark.has(c)).length * 2 > block.length ? 'S' : 's';
      if (ch !== '.') {
        c0 = Math.min(c0, row.length);
        c1 = Math.max(c1, row.length);
        r0 = Math.min(r0, cells.length);
        r1 = Math.max(r1, cells.length);
      }
      row.push(ch);
    }
    cells.push(row);
  }
  for (let r = r0; r <= r1; r++) rows.push(cells[r]!.slice(c0, c1 + 1).join(''));
  return { ox: -X0 - c0, oy: -Y0 - r0, rows };
}

/**
 * The hand shape a pose calls for. `armed` is whether the weapon hand holds a blade or staff: it
 * grips in every fighting pose, where an empty hand makes a fist or strikes with an open palm.
 */
export function handShape(sk: Skeleton, side: Side, armed: boolean): HandShape {
  const { rig } = sk;
  // Hands clasped in front at rest (a graceful bearing) hold one another.
  if (sk.resting === 'front' && !armed) return side === 'A' ? 'clasp' : 'relaxed';
  if (side === 'B') {
    // A paired weapon or a bow keeps the left hand closed round its grip.
    if (armed || rig.far === 'revArm') return 'grip';
    if (rig.far === 'fwd') return 'fingers'; // the off hand's sword fingers in a sword stance
    if (rig.far === 'up' || rig.far === 'face' || rig.far === 'clap' || rig.far === 'clapOpen') return 'palm';
    if (rig.far === 'hip') return 'fist';
    return rig.arm === 'chest' ? 'clasp' : 'relaxed';
  }
  // Pointing at someone (the ชี้หน้า emote): the hand levelled, two fingers out.
  if (rig.arm === 'thrust' && rig.far === 'hip' && !armed) return 'fingers';
  switch (rig.arm) {
    case 'hang':
    case 'back':
    case 'flail':
      // A blade carried at the side is still held.
      return armed && rig.far !== 'revArm' ? 'grip' : 'relaxed';
    case 'chest':
    case 'sit':
      return 'clasp';
    case 'raise':
      return 'fingers';
    case 'fold':
      return 'relaxed';
    case 'face':
    case 'clap':
    case 'clapOpen':
      return armed ? 'grip' : 'palm';
    case 'thrust':
    case 'out':
      return armed ? 'grip' : 'palm';
    default:
      return armed ? 'grip' : 'fist';
  }
}
